import ms from 'ms'
import fetch from '@adc/fetch'
import configment from '@adc/configment'
import { UAParser } from 'ua-parser-js'
import { domainEventsPayload, sendDomainEvents } from './src/domainEvents'

// NOTE: If this becomes an issue, we can make it configurable
const INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD = 10

/**
 * Two conditions for a flush:
 * 1) Time-based. Too much time has elapsed before we last flushed metrics. Active flushing is checking when a metric is
 *    appended, and passive flushing is on a background interval.
 * 2) Volume-based. We want to batch requests up to not lose events.
 *
 * We specifically do not set lastFlushedAt to any to begin with so metrics are stored on the leading edge instead of
 * waiting for the time-base cycle to become valid.
 */
let flusherInterval = null
let activelyFlushing = false
let lastFlushedMetricsAt = null
let lastFlushedDomainEventsAt = null
let pendingMetrics = []
let pendingDomainEvents = []
let postUrl = ''

const initialize = (initOptions = {}) => {
  flusherInterval = window.setInterval(
    flush.bind(null, { force: true }),
    ms('2m')
  )
  postUrl = initOptions.url
}

const haveMetrics = () => pendingMetrics.length || pendingDomainEvents.length

const doneFlushing = () => {
  activelyFlushing = false

  if (!haveMetrics()) return

  if (pendingMetrics.length > 0) {
    lastFlushedMetricsAt = Date.now()
  }

  if (pendingDomainEvents.length > 0) {
    lastFlushedDomainEventsAt = Date.now()
  }

  pendingMetrics = []
  pendingDomainEvents = []
}

const xhr = () => {
  if (activelyFlushing) return
  activelyFlushing = true

  Promise.allSettled([
    ...(pendingMetrics.length === 0
      ? []
      : [
          fetch.post(postUrl, {
            // This is always just best effort. We retry once and that's a wrap. Instrumentation shouldn't negatively impact the
            // application
            tries: 2,
            body: createMetricsPayload(),
          }),
        ]),
    ...(pendingDomainEvents.length === 0
      ? []
      : [sendDomainEvents(pendingDomainEvents)]),
  ]).finally(doneFlushing)
}

const flush = () => {
  if (!haveMetrics()) return

  window.queueMicrotask(xhr)
}

const createDomainEventsPayload = () =>
  pendingDomainEvents.length === 0
    ? null
    : domainEventsPayload(pendingDomainEvents)

const createMetricsPayload = () =>
  pendingMetrics.length === 0
    ? null
    : {
        app_source: configment.get('APP_NAME'),
        metrics: pendingMetrics,
      }

/**
 * Collects page view data for instrumentation in web or React Native.
 * Web: Requires ua-parser-js (CDN: https://unpkg.com/ua-parser-js@1.0.35/dist/ua-parser.min.js).
 * React Native: Requires all relevant data to be passed via options.
 * @param {Object} [options] - Optional parameters for web and React Native.
 * @param {boolean} [options.isReactNative=false] - Whether the environment is React Native.
 * @param {string} [options.routeName] - React Native route/screen name (e.g., "ProductScreen").
 * @param {Record<string, unknown>} [options.routeParams] - React Native route parameters.
 * @param {string} [options.osName] - React Native OS name (e.g., "iOS", "Android").
 * @param {string} [options.osVersion] - React Native OS version (e.g., "18", "14").
 * @param {number} [options.screenWidth] - React Native screen width in pixels.
 * @param {number} [options.screenHeight] - React Native screen height in pixels.
 * @param {boolean} [options.isTablet] - Whether the React Native device is a tablet.
 * @returns {DomainEventPageViewData} Object conforming to the DomainEventPageViewData type.
 */
function collectDomainEventPageViewData({
  isReactNative,
  routeName,
  routeParams,
  osName,
  osVersion,
  screenWidth,
  screenHeight,
  isTablet,
  deviceModel,
  hostName,
} = {}) {
  // Determine client type
  const client_type = isReactNative ? 'native' : 'web'

  // page_search_params
  let page_search_params = {}
  let referrer,
    device_info,
    device_type,
    browser,
    os,
    page_path,
    screen_resolution,
    page_title,
    host_name

  if (client_type === 'web') {
    // page_path
    page_path = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)
    for (const [key, value] of urlParams.entries()) {
      page_search_params[key] = value || undefined
    }
    referrer = document.referrer || undefined

    const uaResult = UAParser(navigator.userAgent)
    device_info = undefined
    device_type = uaResult.device.type || 'other'
    browser = uaResult.browser.name || 'unknown'
    os = `${uaResult.os?.name || 'Unknown'} ${
      uaResult.os?.version || ''
    }`.trim()
    screen_resolution = `${window.screen.width}x${window.screen.height}`
    page_title = window.document.title
    host_name = hostName || location.host
  } else {
    page_path = routeName || 'unknown'
    page_search_params = routeParams || {}
    referrer = undefined
    device_info = deviceModel
    device_type = isTablet ? 'tablet' : 'mobile'
    browser = 'unknown'
    os = `${osName || 'Unknown'} ${osVersion || ''}`.trim()
    screen_resolution = `${Math.round(screenWidth || 0)}x${Math.round(
      screenHeight || 0
    )}`
    host_name = hostName || 'unknown'
  }

  /** @type {DomainEventPageViewData} */
  const data = {
    page_path,
    page_search_params,
    referrer,
    device_info,
    device_type,
    browser,
    os,
    screen_resolution,
    client_type,
    page_title,
    host_name,
  }
  return data
}

const appendMetric = ({ metric, domainEvent }) => {
  if (metric) {
    pendingMetrics.push(metric)
  } else if (domainEvent) {
    domainEvent.time ||= new Date().toISOString()
    domainEvent.service ||= configment.get('APP_NAME')
    if (!domainEvent.data) {
      domainEvent.data = {}
    }
    Object.assign(
      domainEvent.data,
      collectDomainEventPageViewData(domainEvent.data)
    )
    pendingDomainEvents.push(domainEvent)
  }

  const fullQueue =
    pendingMetrics.length > INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD ||
    pendingDomainEvents.length > INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD
  const staleQueue =
    Date.now() - lastFlushedMetricsAt > ms('30s') ||
    Date.now() - lastFlushedDomainEventsAt > ms('30s')

  if (fullQueue || staleQueue) {
    flush()
  }
}

function instrument(metric, tagsOrFunc, maybeFunc) {
  return (maybeFunc || tagsOrFunc)()
}

const createInstrumentFn = initialize => {
  // Attach exports to the default export. This API predates moving to modules, which is why these aren't named exports
  // here.
  // @deprecated - use instrument.initialize instead
  instrument.init = initialize
  instrument.initialize = initialize
  instrument.increment = (name, value = 1, tags = []) =>
    appendMetric({ metric: { type: 'increment', name, value, tags } })
  instrument.decrement = (name, value = 1, tags = []) =>
    appendMetric({ metric: { type: 'decrement', name, value, tags } })
  instrument.gauge = (name, value, tags = []) =>
    appendMetric({ metric: { type: 'gauge', name, value, tags } })
  instrument.histogram = (name, value, tags = []) =>
    appendMetric({ metric: { type: 'histogram', name, value, tags } })
  instrument.domainEvent = domainEvent => appendMetric({ domainEvent })

  return instrument
}

export {
  initialize,
  createMetricsPayload,
  createDomainEventsPayload,
  haveMetrics,
  doneFlushing,
  xhr,
  createInstrumentFn,
}
